<template>
  <Modal :title="title" :showModal="true" class="print-artwork-form">
    <template v-slot:body>
      <div class="col-md-3 d-flex flex-column overflow-auto p-3 pb-5" style="height: 100vh !important">
        <a href="#close" class="close-modal" data-dismiss="modal" @click.prevent="$emit('close', {})"></a>
        <div class="form-group mt-5">
          <label class="font-weight-bold">Page Size:</label>
          <select v-model="selectedPreset" class="form-control" @change="applyPreset">
            <option v-for="preset in paperSizes" :key="preset.value" :value="preset.value">{{ preset.label }}</option>
          </select>
          <div class="input-group mt-2">
            <input type="number" min="1" step="1" v-model.number="paperWidthMm" class="form-control" @change="setCustomSize" />
            <div class="input-group-prepend input-group-append"><span class="input-group-text">x</span></div>
            <input type="number" min="1" step="1" v-model.number="paperHeightMm" class="form-control" @change="setCustomSize" />
            <div class="input-group-append"><span class="input-group-text">mm</span></div>
          </div>
        </div>

        <div class="form-group mt-3">
          <label class="font-weight-bold">DPI:</label>
          <input type="number" min="1" v-model.number="dpi" class="form-control" @change="recalculateLayout" />
        </div>

        <div class="form-group mt-3">
          <label class="font-weight-bold">Page Margin (mm):</label>
          <input type="number" min="0" step="1" v-model.number="pageMarginMm" class="form-control" @change="recalculateLayout" />
        </div>

        <div class="form-group mt-3">
          <label class="font-weight-bold">Item Spacing (mm):</label>
          <input type="number" min="0" step="1" v-model.number="marginMm" class="form-control" @change="recalculateLayout" />
        </div>

        <div class="form-group mt-3">
          <label class="font-weight-bold">BON Diameter (mm):</label>
          <input type="number" min="1" step="1" v-model.number="bonSizeMm" class="form-control" @change="recalculateLayout" />
        </div>

        <div class="mt-4 small text-muted">
          <div>Canvas: {{ canvasWidth }} x {{ canvasHeight }} px</div>
          <div>Used: {{ usedSizeText }}</div>
        </div>

        <div class="mt-4">
          <button class="btn btn-primary w-100 mb-2" @click="downloadCurrentLayer" :disabled="isDownloading">
            <span v-if="isDownloading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            {{ isDownloading ? 'Processing...' : 'Download Current Layer (PNG)' }}
          </button>
          <button class="btn btn-success w-100" @click="downloadAllLayers" :disabled="isDownloading">
            <span v-if="isDownloading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            {{ isDownloading ? 'Processing...' : 'Download All Layers (ZIP)' }}
          </button>
        </div>

        <div class="mt-4" v-if="arrangedItems.length > 0 && options.statuses?.[options.status]?.next?.status?.length > 0 && !hideStatusBtn">
          <h6>Update Status ({{ arrangedItems.length }} items):</h6>
          <div class="mt-2" style="position: relative; z-index: 999">
            <component :is="$options.components.Status" v-if="$options.components.Status" :config="{ data: { more: { can_action: options.status, options } }, manufactureIds: Object.keys(validItems), designIds: Object.keys(validItems) }" @changeValue="handleStatusChange" />
          </div>
        </div>

        <div class="mt-4" v-if="statusUpdateResult && statusUpdateResult.failedItems && statusUpdateResult.failedItems.length > 0">
          <div class="alert alert-danger p-2" style="font-size: 12px">
            <strong>Update Failed ({{ statusUpdateResult.failedItems.length }} items):</strong>
            <ul class="mb-0 mt-1 pl-3">
              <li v-for="fail in statusUpdateResult.failedItems" :key="fail.id">{{ getItemName(fail.id) }}: {{ fail.message }}</li>
            </ul>
          </div>
        </div>

        <div v-if="arrangedItems.length > 0" class="mt-4 flex-grow-1 d-flex flex-column">
          <h6>Items ({{ arrangedItems.length }}):</h6>
          <ul class="list-group overflow-auto flex-grow-1">
            <li v-for="item in arrangedItems" :key="item.id" class="list-group-item p-2" :class="{ active: item.id === selectedItemId }" style="font-size: 12px; cursor: pointer" @click="selectItem(item.id)">
              <div class="d-flex justify-content-between align-items-start">
                <div class="font-weight-bold text-truncate mr-2" :title="items[item.id]?.name || item.id">{{ items[item.id]?.name || item.id }}</div>
                <div class="d-flex flex-nowrap" style="width: 105px">
                  <button class="btn btn-sm btn-outline-info mr-1" style="font-size: 10px; padding: 2px 5px" @click.stop="printQR(item.id)">Print QR</button>
                  <button class="btn btn-sm btn-outline-danger" style="font-size: 10px; padding: 2px 5px" @click.stop="removeItem(item.id)">Remove</button>
                </div>
              </div>
              <div class="mt-1" :class="{ 'text-white': selectedItemId === item.id, 'text-muted': selectedItemId !== item.id }">
                {{ pxToMm(item.imageWidth) }} x {{ pxToMm(item.imageHeight) }} mm
                <span v-if="item.rotated" class="badge badge-warning ml-1">↻</span>
              </div>
            </li>
          </ul>
        </div>
      </div>
      <div class="col-md-9 text-center d-flex flex-column p-3" style="height: 100vh">
        <div class="d-flex justify-content-between align-items-center mb-3" v-show="!loading">
          <ul class="nav nav-pills mb-0">
            <li class="nav-item">
              <a class="nav-link" :class="{ active: selectedLayer === 'front' }" href="#" @click.prevent="((selectedLayer = 'front'), drawActiveLayer())">Front Layer</a>
            </li>
            <li v-if="hasCurrentPageBackLayer" class="nav-item">
              <a class="nav-link" :class="{ active: selectedLayer === 'back' }" href="#" @click.prevent="((selectedLayer = 'back'), drawActiveLayer())">Back Layer</a>
            </li>
            <li class="nav-item">
              <a class="nav-link" :class="{ active: selectedLayer === 'cut' }" href="#" @click.prevent="((selectedLayer = 'cut'), drawActiveLayer())">Cut Layer</a>
            </li>
          </ul>

          <div>
            <span class="mr-3">Zoom:</span>
            <div class="btn-group">
              <button class="btn btn-outline-secondary" @click="zoomOut()" :disabled="zoomLevel <= 10"><i class="ti ti-minus"></i></button>
              <button class="btn btn-outline-secondary font-weight-bold" style="width: 70px" disabled>{{ zoomLevel }}%</button>
              <button class="btn btn-outline-secondary" @click="zoomIn()" :disabled="zoomLevel >= 500"><i class="ti ti-plus"></i></button>
            </div>
          </div>

          <div class="d-flex align-items-center">
            <span class="mr-3">Page: {{ selectedPageIndex + 1 }} / {{ pages.length }}</span>
            <div class="btn-group">
              <button class="btn btn-outline-secondary" @click="selectPage(selectedPageIndex - 1)" :disabled="selectedPageIndex === 0">Prev</button>
              <button class="btn btn-outline-secondary" @click="selectPage(selectedPageIndex + 1)" :disabled="selectedPageIndex >= pages.length - 1">Next</button>
            </div>
          </div>
        </div>
        <div v-if="loading" class="d-flex flex-column align-items-center justify-content-center flex-grow-1" style="width: 100%">
          <div class="spinner-border text-primary" role="status"><span class="sr-only">Loading images...</span></div>
          <p class="mt-3 mb-0 text-muted">Loading &amp; analyzing image dimensions...</p>
          <div v-if="loadingTotal > 0" class="mt-2 text-primary font-weight-bold" style="font-size: 16px">{{ loadingProgress }} / {{ loadingTotal }}</div>
        </div>
        <div ref="canvasWrapper" class="border bg-light p-2 text-center flex-grow-1" :class="{ 'd-none': loading, 'd-flex': !loading }" style="width: 100%; overflow: auto">
          <canvas ref="printCanvas" :style="canvasStyle" @mousedown="startDrag" @mousemove="onMouseMove" @mouseup="stopDrag" @mouseleave="stopDrag"></canvas>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script>
import { saveAs } from 'file-saver'
import print from '@/helper/print.js'

const DEFAULT_PAGE_MARGIN_MM = 1
const DEFAULT_BON_SIZE_MM = 10
const DEFAULT_ITEM_MARGIN_MM = 2
const PAGE_SIZES = [
  { value: 'a4-portrait', label: 'A4 Portrait - 210 x 297 mm', width: 210, height: 297 },
  { value: 'a4-landscape', label: 'A4 Landscape - 297 x 210 mm', width: 297, height: 210 },
  { value: 'a3-portrait', label: 'A3 Portrait - 297 x 420 mm', width: 297, height: 420 },
  { value: 'a3-landscape', label: 'A3 Landscape - 420 x 297 mm', width: 420, height: 297 },
  { value: 'custom', label: 'Custom', width: 210, height: 297 }
]
const PAGE_SETTING_COOKIE_KEY = 'print_artwork_page_settings'
const PAGE_SETTING_FIELDS = ['selectedPreset', 'paperWidthMm', 'paperHeightMm', 'dpi', 'pageMarginMm', 'marginMm', 'bonSizeMm']
const PAGE_SETTING_MIN_VALUES = {
  paperWidthMm: 1,
  paperHeightMm: 1,
  dpi: 1,
  pageMarginMm: 0,
  marginMm: 0,
  bonSizeMm: 1
}

class MaxRectsPacker {
  constructor(width, height) {
    this.binWidth = width
    this.binHeight = height
    this.freeRectangles = [{ x: 0, y: 0, width, height }]
    this.usedRectangles = []
  }

  insert(width, height) {
    let bestNode = this.scoreRect(width, height)
    if (bestNode.score1 === Number.MAX_VALUE) return null

    this.placeRect(bestNode)
    return bestNode
  }

  scoreRect(width, height) {
    let bestNode = {
      x: 0,
      y: 0,
      width: 0,
      height: 0,
      rotated: false,
      score1: Number.MAX_VALUE,
      score2: Number.MAX_VALUE,
      score3: Number.MAX_VALUE,
      score4: Number.MAX_VALUE
    }

    const isLandscape = this.binWidth >= this.binHeight

    const evaluate = (w, h, rotated) => {
      for (let i = 0; i < this.freeRectangles.length; i++) {
        let freeRect = this.freeRectangles[i]
        if (freeRect.width >= w && freeRect.height >= h) {
          let score1 = isLandscape ? freeRect.x : freeRect.y
          let score2 = isLandscape ? freeRect.y : freeRect.x

          let leftoverX = freeRect.width - w
          let leftoverY = freeRect.height - h
          let score3 = Math.min(leftoverX, leftoverY)
          let score4 = Math.max(leftoverX, leftoverY)

          if (score1 < bestNode.score1 || (score1 === bestNode.score1 && score2 < bestNode.score2) || (score1 === bestNode.score1 && score2 === bestNode.score2 && score3 < bestNode.score3) || (score1 === bestNode.score1 && score2 === bestNode.score2 && score3 === bestNode.score3 && score4 < bestNode.score4)) {
            bestNode.x = freeRect.x
            bestNode.y = freeRect.y
            bestNode.width = w
            bestNode.height = h
            bestNode.score1 = score1
            bestNode.score2 = score2
            bestNode.score3 = score3
            bestNode.score4 = score4
            bestNode.rotated = rotated
          }
        }
      }
    }

    evaluate(width, height, false)
    if (width !== height) {
      evaluate(height, width, true)
    }

    return bestNode
  }

  placeRect(node) {
    let numRectanglesToProcess = this.freeRectangles.length
    for (let i = 0; i < numRectanglesToProcess; i++) {
      if (this.splitFreeNode(this.freeRectangles[i], node)) {
        this.freeRectangles.splice(i, 1)
        i--
        numRectanglesToProcess--
      }
    }
    this.pruneFreeList()
    this.usedRectangles.push(node)
  }

  splitFreeNode(freeNode, usedNode) {
    if (usedNode.x >= freeNode.x + freeNode.width || usedNode.x + usedNode.width <= freeNode.x || usedNode.y >= freeNode.y + freeNode.height || usedNode.y + usedNode.height <= freeNode.y) {
      return false
    }

    if (usedNode.x < freeNode.x + freeNode.width && usedNode.x + usedNode.width > freeNode.x) {
      if (usedNode.y > freeNode.y && usedNode.y < freeNode.y + freeNode.height) {
        let newNode = { ...freeNode, height: usedNode.y - freeNode.y }
        this.freeRectangles.push(newNode)
      }
      if (usedNode.y + usedNode.height < freeNode.y + freeNode.height) {
        let newNode = { ...freeNode, y: usedNode.y + usedNode.height, height: freeNode.y + freeNode.height - (usedNode.y + usedNode.height) }
        this.freeRectangles.push(newNode)
      }
    }

    if (usedNode.y < freeNode.y + freeNode.height && usedNode.y + usedNode.height > freeNode.y) {
      if (usedNode.x > freeNode.x && usedNode.x < freeNode.x + freeNode.width) {
        let newNode = { ...freeNode, width: usedNode.x - freeNode.x }
        this.freeRectangles.push(newNode)
      }
      if (usedNode.x + usedNode.width < freeNode.x + freeNode.width) {
        let newNode = { ...freeNode, x: usedNode.x + usedNode.width, width: freeNode.x + freeNode.width - (usedNode.x + usedNode.width) }
        this.freeRectangles.push(newNode)
      }
    }

    return true
  }

  pruneFreeList() {
    for (let i = 0; i < this.freeRectangles.length; i++) {
      for (let j = i + 1; j < this.freeRectangles.length; j++) {
        if (this.isContainedIn(this.freeRectangles[i], this.freeRectangles[j])) {
          this.freeRectangles.splice(i, 1)
          i--
          break
        }
        if (this.isContainedIn(this.freeRectangles[j], this.freeRectangles[i])) {
          this.freeRectangles.splice(j, 1)
          j--
        }
      }
    }
  }

  isContainedIn(a, b) {
    return a.x >= b.x && a.y >= b.y && a.x + a.width <= b.x + b.width && a.y + a.height <= b.y + b.height
  }
}

class GAPacker {
  static packSequence(sequence, cw, ch, pageMargin, margin) {
    const pages = [] // Array of { packer, items: [] }
    const isLandscape = cw >= ch

    for (let i = 0; i < sequence.length; i++) {
      let artwork = sequence[i]
      let w = artwork.imageWidth + margin
      let h = artwork.imageHeight + margin

      let node = null
      let targetPageIdx = -1

      // 1. First-Fit: Try to fit into previously opened pages (utilize empty space)
      for (let j = 0; j < pages.length; j++) {
        if (pages[j].packer) {
          node = pages[j].packer.insert(w, h)
          if (node) {
            targetPageIdx = j
            break
          }
        }
      }

      // 2. If it doesn't fit in any existing page, create a new page
      if (!node) {
        let newPacker = new MaxRectsPacker(cw - 2 * pageMargin + margin, ch - 2 * pageMargin + margin)
        node = newPacker.insert(w, h)
        if (node) {
          pages.push({ packer: newPacker, items: [{ artwork, node }] })
        } else {
          // Case when image is larger than the entire canvas
          pages.push({ packer: null, items: [{ artwork, node: { x: 0, y: 0, width: w, height: h, rotated: false } }] })
        }
      } else {
        pages[targetPageIdx].items.push({ artwork, node })
      }
    }

    let totalUsed = 0
    for (let p of pages) {
      let maxUsed = 0
      for (let item of p.items) {
        if (isLandscape) {
          maxUsed = Math.max(maxUsed, item.node.x + item.node.width)
        } else {
          maxUsed = Math.max(maxUsed, item.node.y + item.node.height)
        }
      }
      totalUsed += maxUsed
    }

    // Priority 1: Minimum number of pages (pages.length is extremely important)
    // Priority 2: Minimize the extent used along the long edge (to leave the largest continuous free area)
    let cost = totalUsed + pages.length * 1000000000

    return { pages: pages.map((p) => p.items), cost }
  }

  static run(artworks, cw, ch, pageMargin, margin, popSize = 50, generations = 100) {
    if (artworks.length === 0) return { pages: [], cost: 0 }

    // Evaluate multiple initial sorts to find the best seed
    let sortOptions = [
      (a, b) => b.imageWidth * b.imageHeight - a.imageWidth * a.imageHeight, // Area Descending
      (a, b) => Math.max(b.imageWidth, b.imageHeight) - Math.max(a.imageWidth, a.imageHeight), // Max Dimension Descending
      (a, b) => b.imageWidth + b.imageHeight - (a.imageWidth + a.imageHeight) // Perimeter Descending
    ]

    let population = []
    let bestSeq = null
    let bestRes = { cost: Infinity }

    // 1. Initialize population with heuristics and their variations
    for (let i = 0; i < sortOptions.length; i++) {
      let seq = [...artworks].sort(sortOptions[i])
      let res = this.packSequence(seq, cw, ch, pageMargin, margin)
      population.push({ seq, cost: res.cost })
      if (res.cost < bestRes.cost) {
        bestSeq = [...seq]
        bestRes = res
      }
    }

    // Fill the rest of the population with random mutations of the best heuristic
    while (population.length < popSize) {
      let newSeq = [...population[0].seq]
      // Mutate 1 to 3 times to create diversity
      let mutations = 1 + Math.floor(Math.random() * 3)
      for (let m = 0; m < mutations; m++) {
        this.mutate(newSeq)
      }
      let res = this.packSequence(newSeq, cw, ch, pageMargin, margin)
      population.push({ seq: newSeq, cost: res.cost })
      if (res.cost < bestRes.cost) {
        bestSeq = [...newSeq]
        bestRes = res
      }
    }

    // 2. Evolution Loop
    for (let gen = 0; gen < generations; gen++) {
      // Sort by cost ASC (lower is better)
      population.sort((a, b) => a.cost - b.cost)

      let nextGen = []

      // Elitism: Keep top 10% of the population untouched
      let eliteCount = Math.max(1, Math.floor(popSize * 0.1))
      for (let i = 0; i < eliteCount; i++) {
        nextGen.push(population[i])
      }

      // Crossover and mutation for the rest
      while (nextGen.length < popSize) {
        // Tournament Selection
        let parent1 = this.tournamentSelection(population)
        let parent2 = this.tournamentSelection(population)

        // Partially Matched Crossover (PMX)
        let childSeq = this.crossoverPMX(parent1.seq, parent2.seq)

        // 30% chance to mutate the child
        if (Math.random() < 0.3) {
          this.mutate(childSeq)
        }

        let res = this.packSequence(childSeq, cw, ch, pageMargin, margin)
        nextGen.push({ seq: childSeq, cost: res.cost })

        if (res.cost < bestRes.cost) {
          bestSeq = [...childSeq]
          bestRes = res
        }
      }

      population = nextGen
    }

    return this.packSequence(bestSeq, cw, ch, pageMargin, margin)
  }

  // Selects the best among 3 random contestants
  static tournamentSelection(population) {
    let best = population[Math.floor(Math.random() * population.length)]
    for (let i = 1; i < 3; i++) {
      let contestant = population[Math.floor(Math.random() * population.length)]
      if (contestant.cost < best.cost) {
        best = contestant
      }
    }
    return best
  }

  // Swap or Insert mutation
  static mutate(seq) {
    if (seq.length <= 1) return
    if (Math.random() < 0.6) {
      // Swap 2 items
      let idx1 = Math.floor(Math.random() * seq.length)
      let idx2 = Math.floor(Math.random() * seq.length)
      let temp = seq[idx1]
      seq[idx1] = seq[idx2]
      seq[idx2] = temp
    } else {
      // Insert item at new position
      let idx1 = Math.floor(Math.random() * seq.length)
      let idx2 = Math.floor(Math.random() * seq.length)
      let item = seq.splice(idx1, 1)[0]
      seq.splice(idx2, 0, item)
    }
  }

  // Partially Matched Crossover (PMX) for permutations
  static crossoverPMX(p1, p2) {
    let len = p1.length
    if (len <= 1) return [...p1]

    let start = Math.floor(Math.random() * len)
    let end = Math.floor(Math.random() * len)
    if (start > end) {
      let tmp = start
      start = end
      end = tmp
    }

    let child = new Array(len).fill(null)
    let p1Map = new Map()

    // Copy segment from p1
    for (let i = start; i <= end; i++) {
      child[i] = p1[i]
      p1Map.set(p1[i].id, true)
    }

    // Fill the rest with p2
    let currIdx = 0
    for (let i = 0; i < len; i++) {
      if (!p1Map.has(p2[i].id)) {
        while (child[currIdx] !== null) currIdx++
        child[currIdx] = p2[i]
      }
    }

    return child
  }
}

export default {
  props: {
    config: Object
  },
  data() {
    return {
      selectedLayer: 'front',
      selectedPreset: 'a3-landscape',
      paperWidthMm: 420,
      paperHeightMm: 297,
      dpi: 300,
      marginMm: DEFAULT_ITEM_MARGIN_MM,
      pageMarginMm: DEFAULT_PAGE_MARGIN_MM,
      bonSizeMm: DEFAULT_BON_SIZE_MM,
      loading: false,
      loadingProgress: 0,
      loadingTotal: 0,
      isDownloading: false,
      hideStatusBtn: false,
      statusUpdateResult: null,
      artworksData: [],
      pages: [],
      selectedPageIndex: 0,
      selectedItemId: null,
      dragState: null,
      canvasScale: 1,
      zoomLevel: 100,
      isAltPressed: false,
      isPanning: false,
      pannedDuringAlt: false,
      panStart: { x: 0, y: 0, scrollX: 0, scrollY: 0 },
      paperSizes: PAGE_SIZES
    }
  },
  computed: {
    title() {
      return 'Print Artwork - Layout & Arrange'
    },
    canvasWidth() {
      return this.mmToPx(this.paperWidthMm)
    },
    canvasHeight() {
      return this.mmToPx(this.paperHeightMm)
    },
    marginPx() {
      return this.mmToPx(this.marginMm)
    },
    pageMarginPx() {
      return this.mmToPx(this.pageMarginMm + this.bonSizeMm)
    },
    items() {
      return this.config?.items || {}
    },
    canAction() {
      return this.config?.can_action
    },
    options() {
      return this.config?.options || {}
    },
    currentPage() {
      return this.pages[this.selectedPageIndex] || { items: [], usedWidth: 0, usedHeight: 0 }
    },
    arrangedItems() {
      return this.currentPage.items
    },
    hasCurrentPageBackLayer() {
      return this.arrangedItems.some((item) => item.data?.sourceUrls?.back)
    },
    pageSettings() {
      return PAGE_SETTING_FIELDS.reduce((acc, field) => {
        acc[field] = this[field]
        return acc
      }, {})
    },
    usedSizeText() {
      return `${this.pxToMm(this.currentPage.usedWidth)} x ${this.pxToMm(this.currentPage.usedHeight)} mm`
    },
    canvasStyle() {
      let cursor = 'default'
      if (this.isPanning) cursor = 'grabbing'
      else if (this.isAltPressed) cursor = 'grab'

      return {
        display: 'block',
        margin: 'auto',
        width: `${Math.round(this.canvasWidth * this.canvasScale * (this.zoomLevel / 100))}px`,
        height: `${Math.round(this.canvasHeight * this.canvasScale * (this.zoomLevel / 100))}px`,
        border: '1px dashed #666',
        cursor: cursor,
        maxWidth: 'none'
      }
    },
    validItems() {
      return Object.entries(this.config?.items || {}).reduce((acc, [id, item]) => {
        const layers = item?.artworks || {}
        if (layers.front || layers.back || layers.cut) acc[id] = item
        return acc
      }, {})
    }
  },
  mounted() {
    window.addEventListener('resize', this.updateCanvasScale)
    window.addEventListener('keydown', this.handleKeyDown)
    window.addEventListener('keyup', this.handleKeyUp)
    if (this.$refs.canvasWrapper) {
      this.$refs.canvasWrapper.addEventListener('wheel', this.handleWheel, { passive: false })
    }
    this.initPageSettings()
    this.loadImagesAndArrange()
  },
  unmounted() {
    window.removeEventListener('resize', this.updateCanvasScale)
    window.removeEventListener('keydown', this.handleKeyDown)
    window.removeEventListener('keyup', this.handleKeyUp)
    if (this.$refs.canvasWrapper) {
      this.$refs.canvasWrapper.removeEventListener('wheel', this.handleWheel)
    }
  },
  watch: {
    pageSettings(value) {
      this.$util.cookie.set(PAGE_SETTING_COOKIE_KEY, value, 7)
    }
  },
  methods: {
    initPageSettings() {
      const settings = this.$util.cookie.get(PAGE_SETTING_COOKIE_KEY)
      if (!settings || typeof settings !== 'object') return
      PAGE_SETTING_FIELDS.forEach((field) => this.applyPageSetting(field, settings[field]))
    },
    applyPageSetting(field, value) {
      if (value === undefined || value === '') return
      if (field === 'selectedPreset') {
        if (this.paperSizes.some((preset) => preset.value === value)) this.selectedPreset = value
        return
      }

      const numberValue = Math.max(Number(value), PAGE_SETTING_MIN_VALUES[field])
      if (Number.isFinite(numberValue)) this[field] = numberValue
    },
    getItemName(id) {
      return this.items[id]?.name || id
    },
    handleStatusChange(result) {
      if (result) {
        this.statusUpdateResult = result
        this.hideStatusBtn = true
      }
    },
    printQR(id) {
      const order = this.items[id]?.order
      if (!order) {
        alert("This item does not have refer order.")
        return
      }
      print.order(order)
    },
    removeItem(id) {
      const artworkIndex = this.artworksData.findIndex((d) => d.id === id)
      if (artworkIndex !== -1) {
        this.artworksData.splice(artworkIndex, 1)
        if (this.selectedItemId === id) {
          this.selectedItemId = null
        }
        this.recalculateLayout()
      }
    },
    closeModal() {
      this.$emit('close')
    },
    applyPreset() {
      const preset = this.paperSizes.find((p) => p.value === this.selectedPreset)
      if (preset && preset.value !== 'custom') {
        this.paperWidthMm = preset.width
        this.paperHeightMm = preset.height
      }
      this.recalculateLayout()
    },
    setCustomSize() {
      this.selectedPreset = 'custom'
      this.recalculateLayout()
    },
    mmToPx(v) {
      return Math.round(((Number(v) || 0) * (Number(this.dpi) || 300)) / 25.4)
    },
    pxToMm(v) {
      return (((Number(v) || 0) * 25.4) / (Number(this.dpi) || 300)).toFixed(1)
    },

    // ─── Data Loading ──────────────────────────────────────────────────────────

    getArtworkSources() {
      return Object.entries(this.validItems).reduce((acc, [id, item]) => {
        acc[id] = item?.artworks || {}
        return acc
      }, {})
    },
    loadImage(url) {
      return new Promise((resolve) => {
        if (!url) return resolve(null)
        const img = new Image()
        img.crossOrigin = 'anonymous'
        img.onload = () => resolve(img)
        img.onerror = () => resolve(null)
        img.src = url
      })
    },

    async loadImagesAndArrange() {
      this.loading = true
      this.loadingProgress = 0
      this.loadingTotal = 0
      this.artworksData = []
      this.pages = []
      try {
        const sources = this.getArtworkSources()
        const entries = Object.entries(sources)
        this.loadingTotal = entries.length

        const artworks = await Promise.all(
          entries.map(async ([id, layers]) => {
            const firstUrl = layers.front || layers.back || layers.cut
            if (!firstUrl) {
              this.loadingProgress++
              return null
            }

            const firstImage = await this.loadImage(firstUrl)
            if (!firstImage) {
              this.loadingProgress++
              return null
            }

            const artwork = {
              id,
              imageWidth: firstImage.width,
              imageHeight: firstImage.height,
              layers: { front: null, back: null, cut: null },
              sourceUrls: layers,
              promises: {}
            }

            if (layers.front === firstUrl) artwork.layers.front = firstImage
            else if (layers.back === firstUrl) artwork.layers.back = firstImage
            else if (layers.cut === firstUrl) artwork.layers.cut = firstImage

            const loadLayer = (layerName) => {
              const url = layers[layerName]
              if (!url) return
              if (artwork.layers[layerName]) {
                artwork.promises[layerName] = Promise.resolve(artwork.layers[layerName])
              } else {
                artwork.promises[layerName] = this.loadImage(url).then((img) => {
                  artwork.layers[layerName] = img
                  if (this.selectedLayer === layerName) this.drawActiveLayer()
                  return img
                })
              }
            }

            loadLayer('front')
            loadLayer('back')
            loadLayer('cut')

            this.loadingProgress++
            return artwork
          })
        )

        this.artworksData = artworks.filter(Boolean)
        this.recalculateLayout()
      } catch (err) {
        console.error(err)
        alert('An error occurred loading images.')
      } finally {
        this.loading = false
        this.loadingProgress = 0
        this.loadingTotal = 0
        this.$nextTick(this.updateCanvasScale)
      }
    },

    // ─── Layout: Block Factory + Non-Rectangular Grid Packing ─────────────────

    recalculateLayout() {
      if (this.artworksData.length === 0) {
        this.pages = []
        this.$nextTick(this.updateCanvasScale)
        return
      }

      const oldPages = this.pages || []
      const oldSelectedPageItems = oldPages[this.selectedPageIndex]?.items || []
      const oldSelectedPageIds = new Set(oldSelectedPageItems.map((it) => it.id))

      this.pages = this.packAllPages()

      if (oldSelectedPageIds.size > 0 && this.pages.length > 0) {
        let bestIndex = 0
        let maxScore = 0
        for (let i = 0; i < this.pages.length; i++) {
          let score = 0
          for (let item of this.pages[i].items) {
            if (oldSelectedPageIds.has(item.id)) score += item.areaPercent || 0
          }
          if (score > maxScore) {
            maxScore = score
            bestIndex = i
          }
        }
        this.selectedPageIndex = bestIndex
      } else {
        this.selectedPageIndex = Math.min(this.selectedPageIndex, Math.max(this.pages.length - 1, 0))
      }

      if (this.pages.length > 0) {
        this.selectedItemId = null
      }
      this.$nextTick(this.updateCanvasScale)
    },

    packAllPages() {
      // Use GAPacker to find the optimal arrangement using Genetic Algorithm
      // popSize = 50, generations = 100 gives 5000 evaluations.
      const bestRes = GAPacker.run(this.artworksData, this.canvasWidth, this.canvasHeight, this.pageMarginPx, this.marginPx, 50, 100)

      const pages = []
      for (let p of bestRes.pages) {
        let placed = []
        for (let item of p) {
          const d = item.artwork
          const node = item.node
          const isRotated = node.rotated
          const w = isRotated ? d.imageHeight : d.imageWidth
          const h = isRotated ? d.imageWidth : d.imageHeight

          const areaPercent = ((w * h) / (this.canvasWidth * this.canvasHeight)) * 100

          const placedItem = {
            ...d,
            id: d.id,
            x: this.pageMarginPx + node.x,
            y: this.pageMarginPx + node.y,
            rotated: isRotated,
            w: w,
            h: h,
            imgX: 0,
            imgY: 0,
            rects: [{ x: 0, y: 0, w: w, h: h }],
            data: d,
            areaPercent: areaPercent
          }
          placed.push(placedItem)
        }
        pages.push(this.createPageLayout(placed))
      }

      if (this.pages && this.pages.length > 0) {
        let mappings = []
        for (let i = 0; i < pages.length; i++) {
          for (let j = 0; j < this.pages.length; j++) {
            let score = 0
            let oldItemAreas = {}
            for (let oldIt of this.pages[j].items) {
              oldItemAreas[oldIt.id] = oldIt.areaPercent || ((oldIt.w * oldIt.h) / (this.canvasWidth * this.canvasHeight)) * 100
            }

            for (let item of pages[i].items) {
              if (oldItemAreas[item.id]) {
                score += oldItemAreas[item.id]
              }
            }
            if (score > 0) {
              mappings.push({ newIdx: i, oldIdx: j, score })
            }
          }
        }
        mappings.sort((a, b) => b.score - a.score)

        let assignedNew = new Set()
        let assignedOld = new Set()
        let pageAtOldIdx = []

        for (let m of mappings) {
          if (!assignedNew.has(m.newIdx) && !assignedOld.has(m.oldIdx)) {
            assignedNew.add(m.newIdx)
            assignedOld.add(m.oldIdx)
            pageAtOldIdx[m.oldIdx] = pages[m.newIdx]
          }
        }

        let unassignedNew = []
        for (let i = 0; i < pages.length; i++) {
          if (!assignedNew.has(i)) unassignedNew.push(pages[i])
        }

        let finalPages = []
        for (let i = 0; i < Math.max(this.pages.length, pages.length); i++) {
          if (pageAtOldIdx[i]) {
            finalPages.push(pageAtOldIdx[i])
          } else if (unassignedNew.length > 0) {
            finalPages.push(unassignedNew.shift())
          }
        }
        while (unassignedNew.length > 0) finalPages.push(unassignedNew.shift())

        return finalPages.filter(Boolean)
      }

      return pages
    },

    // ─── Rect Helpers ──────────────────────────────────────────────────────────

    createPageLayout(items) {
      let maxX = 0
      let maxY = 0
      items.forEach((it) => {
        maxX = Math.max(maxX, it.x + it.w)
        maxY = Math.max(maxY, it.y + it.h)
      })
      return { items, usedWidth: maxX, usedHeight: maxY }
    },

    // ─── Page & Item Selection ─────────────────────────────────────────────────

    selectPage(index) {
      this.selectedPageIndex = Math.max(0, Math.min(index, this.pages.length - 1))
      this.selectedItemId = null
      this.$nextTick(this.drawActiveLayer)
    },
    selectItem(id) {
      if (this.selectedItemId === id) {
        this.selectedItemId = null
      } else {
        this.selectedItemId = id
      }
      this.drawActiveLayer()
    },

    // ─── Drag & Drop ───────────────────────────────────────────────────────────

    getCanvasPoint(event) {
      const canvas = this.$refs.printCanvas
      const rect = canvas.getBoundingClientRect()
      return {
        x: ((event.clientX - rect.left) * this.canvasWidth) / rect.width,
        y: ((event.clientY - rect.top) * this.canvasHeight) / rect.height
      }
    },
    /** Find item at canvas point (checking internal rects). */
    findItemAt(point) {
      return [...this.arrangedItems].reverse().find((item) => {
        return item.rects.some((r) => {
          const x = item.x + r.x
          const y = item.y + r.y
          return point.x >= x && point.x <= x + r.w && point.y >= y && point.y <= y + r.h
        })
      })
    },
    startDrag(event) {
      if (this.isAltPressed) {
        this.isPanning = true
        const wrapper = this.$refs.canvasWrapper
        this.panStart = {
          x: event.clientX,
          y: event.clientY,
          scrollX: wrapper.scrollLeft,
          scrollY: wrapper.scrollTop
        }
        return
      }

      const point = this.getCanvasPoint(event)
      const item = this.findItemAt(point)
      if (!item) {
        this.selectedItemId = null
        this.drawActiveLayer()
        return
      }
      this.dragState = {
        id: item.id,
        offsetX: Math.round(point.x - item.x),
        offsetY: Math.round(point.y - item.y),
        startX: item.x,
        startY: item.y,
        lastValidX: item.x,
        lastValidY: item.y,
        startClientX: event.clientX,
        startClientY: event.clientY,
        moved: false,
        originallySelected: this.selectedItemId === item.id
      }
      this.selectedItemId = item.id
      this.drawActiveLayer()
    },
    onMouseMove(event) {
      if (this.isPanning) {
        this.pannedDuringAlt = true
        const wrapper = this.$refs.canvasWrapper
        const dx = event.clientX - this.panStart.x
        const dy = event.clientY - this.panStart.y
        wrapper.scrollLeft = this.panStart.scrollX - dx
        wrapper.scrollTop = this.panStart.scrollY - dy
        return
      }

      this.currentMousePoint = this.getCanvasPoint(event)
      if (!this.dragState) return

      if (!this.dragState.moved) {
        const dist = Math.sqrt(Math.pow(event.clientX - this.dragState.startClientX, 2) + Math.pow(event.clientY - this.dragState.startClientY, 2))
        if (dist <= 3) return
        this.dragState.moved = true
      }

      const point = this.currentMousePoint
      const item = this.arrangedItems.find((it) => it.id === this.dragState.id)
      if (!item) return

      const nx = Math.round(Math.max(0, Math.min(this.canvasWidth - item.w, point.x - this.dragState.offsetX)))
      const ny = Math.round(Math.max(0, Math.min(this.canvasHeight - item.h, point.y - this.dragState.offsetY)))

      item.x = nx
      item.y = ny

      if (!this.isDragOverlapping(item, nx, ny)) {
        this.dragState.lastValidX = nx
        this.dragState.lastValidY = ny
      }

      this.currentPage.usedWidth = 0
      this.currentPage.usedHeight = 0
      this.arrangedItems.forEach((it) => {
        this.currentPage.usedWidth = Math.max(this.currentPage.usedWidth, it.x + it.w)
        this.currentPage.usedHeight = Math.max(this.currentPage.usedHeight, it.y + it.h)
      })
      this.drawActiveLayer()
    },
    /** Check if moving item to (nx, ny) overlaps with other items based on sub-rects. */
    isDragOverlapping(item, nx, ny) {
      return this.arrangedItems.some((other) => {
        if (other.id === item.id) return false
        for (const myR of item.rects) {
          for (const otR of other.rects) {
            const mx = nx + myR.x
            const my = ny + myR.y
            const ox = other.x + otR.x
            const oy = other.y + otR.y

            // 1 pixel tolerance to handle floating point precision issues
            // and allow placing images edge-to-edge (0px gap) without triggering overlap error.
            const tol = 1
            if (mx < ox + otR.w - tol && mx + myR.w > ox + tol && my < oy + otR.h - tol && my + myR.h > oy + tol) {
              return true
            }
          }
        }
        return false
      })
    },
    stopDrag() {
      if (this.isPanning) {
        this.isPanning = false
        return
      }

      if (this.dragState) {
        const item = this.arrangedItems.find((it) => it.id === this.dragState.id)
        if (item && this.isDragOverlapping(item, item.x, item.y)) {
          // Revert to the last valid position
          item.x = this.dragState.lastValidX !== undefined ? this.dragState.lastValidX : this.dragState.startX
          item.y = this.dragState.lastValidY !== undefined ? this.dragState.lastValidY : this.dragState.startY
        }

        if (!this.dragState.moved && this.dragState.originallySelected) {
          this.selectedItemId = null
        }
      }
      this.dragState = null
      this.drawActiveLayer()
    },

    rotateSelectedItem() {
      if (!this.selectedItemId) return
      let targetItem = this.arrangedItems.find((it) => it.id === this.selectedItemId)

      if (targetItem) {
        targetItem.rotated = !targetItem.rotated
        const oldW = targetItem.w
        targetItem.w = targetItem.h
        targetItem.h = oldW

        if (targetItem.rects && targetItem.rects.length > 0) {
          targetItem.rects[0].w = targetItem.w
          targetItem.rects[0].h = targetItem.h
        }

        targetItem.x = Math.max(0, Math.min(this.canvasWidth - targetItem.w, targetItem.x))
        targetItem.y = Math.max(0, Math.min(this.canvasHeight - targetItem.h, targetItem.y))

        this.currentPage.usedWidth = 0
        this.currentPage.usedHeight = 0
        this.arrangedItems.forEach((it) => {
          this.currentPage.usedWidth = Math.max(this.currentPage.usedWidth, it.x + it.w)
          this.currentPage.usedHeight = Math.max(this.currentPage.usedHeight, it.y + it.h)
        })
        this.drawActiveLayer()
      }
    },

    handleKeyDown(event) {
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') return

      if (event.code === 'Space') {
        event.preventDefault()
        if (!event.repeat) {
          this.rotateSelectedItem()
        }
      } else if (event.key === 'Alt') {
        event.preventDefault()
        if (!event.repeat) {
          this.isAltPressed = true
          this.pannedDuringAlt = false
        }
      }
    },
    handleKeyUp(event) {
      if (event.key === 'Alt') {
        this.isAltPressed = false
        this.isPanning = false
      }
    },
    setZoom(newZoom, event = null) {
      if (newZoom === this.zoomLevel) return

      const wrapper = this.$refs.canvasWrapper
      const canvas = this.$refs.printCanvas
      if (!wrapper || !canvas) {
        this.zoomLevel = newZoom
        return
      }

      const f = newZoom / this.zoomLevel

      let canvasMouseX = 0
      let canvasMouseY = 0
      let targetClientX = 0
      let targetClientY = 0

      const wrapperRect = wrapper.getBoundingClientRect()
      const canvasRect = canvas.getBoundingClientRect()

      if (event) {
        if (event.clientX >= wrapperRect.left && event.clientX <= wrapperRect.right && event.clientY >= wrapperRect.top && event.clientY <= wrapperRect.bottom) {
          targetClientX = event.clientX
          targetClientY = event.clientY
        } else {
          targetClientX = wrapperRect.left + wrapperRect.width / 2
          targetClientY = wrapperRect.top + wrapperRect.height / 2
        }
      } else {
        targetClientX = wrapperRect.left + wrapperRect.width / 2
        targetClientY = wrapperRect.top + wrapperRect.height / 2
      }

      canvasMouseX = targetClientX - canvasRect.left
      canvasMouseY = targetClientY - canvasRect.top

      this.zoomLevel = newZoom

      this.$nextTick(() => {
        const newRect = canvas.getBoundingClientRect()
        const newScreenX = newRect.left + canvasMouseX * f
        const newScreenY = newRect.top + canvasMouseY * f

        const diffX = newScreenX - targetClientX
        const diffY = newScreenY - targetClientY

        wrapper.scrollLeft += diffX
        wrapper.scrollTop += diffY
      })
    },
    zoomIn(event = null) {
      this.setZoom(Math.min(this.zoomLevel + 10, 500), event)
    },
    zoomOut(event = null) {
      this.setZoom(Math.max(this.zoomLevel - 10, 10), event)
    },
    handleWheel(event) {
      if (this.isAltPressed) {
        event.preventDefault()
        if (event.deltaY < 0) {
          this.zoomIn(event)
        } else {
          this.zoomOut(event)
        }
      }
    },

    // ─── Canvas Drawing ────────────────────────────────────────────────────────

    drawActiveLayer() {
      const canvas = this.$refs.printCanvas
      if (!canvas) return
      if (this.selectedLayer === 'back' && !this.hasCurrentPageBackLayer) {
        this.selectedLayer = 'front'
      }
      this.drawLayer(this.selectedLayer, canvas, this.arrangedItems, true, true)
    },
    updateCanvasScale() {
      const wrapper = this.$refs.canvasWrapper
      if (!wrapper || !this.canvasWidth || !this.canvasHeight) return
      const aw = Math.max(wrapper.clientWidth - 16, 1)
      const ah = Math.max(wrapper.clientHeight - 16, 1)
      this.canvasScale = Math.min(aw / this.canvasWidth, ah / this.canvasHeight, 1)

      const canvas = this.$refs.printCanvas
      if (canvas) {
        const dpr = Math.max(window.devicePixelRatio || 1, 2.5)
        canvas.width = Math.round(this.canvasWidth * this.canvasScale * dpr)
        canvas.height = Math.round(this.canvasHeight * this.canvasScale * dpr)
      }
      this.drawActiveLayer()
    },

    /**
     * Draw a full layer: back is mirrored.
     */
    drawLayer(layer, canvas, items, showSelection = false, isPreview = false) {
      const ctx = canvas.getContext('2d')

      ctx.save()
      if (isPreview) {
        const dpr = Math.max(window.devicePixelRatio || 1, 2.5)
        ctx.scale(this.canvasScale * dpr, this.canvasScale * dpr)
      }

      ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight)
      ctx.fillStyle = '#fff'
      ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight)

      const isBack = layer === 'back'
      if (isBack) {
        ctx.save()
        ctx.translate(this.canvasWidth, 0)
        ctx.scale(-1, 1)
      }

      items.forEach((item) => this.drawItem(ctx, item, layer))

      if (items.length > 0) {
        let minX = Infinity,
          minY = Infinity,
          maxX = -Infinity,
          maxY = -Infinity
        items.forEach((item) => {
          const drawX = item.x + item.imgX
          const drawY = item.y + item.imgY
          const drawW = item.rotated ? item.imageHeight : item.imageWidth
          const drawH = item.rotated ? item.imageWidth : item.imageHeight
          if (drawX < minX) minX = drawX
          if (drawY < minY) minY = drawY
          if (drawX + drawW > maxX) maxX = drawX + drawW
          if (drawY + drawH > maxY) maxY = drawY + drawH
        })

        // Draw BON (black corner crop marks) on the outer bounding box
        const rx = this.mmToPx((Number(this.bonSizeMm) || DEFAULT_BON_SIZE_MM) / 2)
        this.drawBon(ctx, minX - rx, minY - rx)
        this.drawBon(ctx, maxX + rx, minY - rx)
        this.drawBon(ctx, minX - rx, maxY + rx)
        this.drawBon(ctx, maxX + rx, maxY + rx)
      }

      if (isBack) ctx.restore()

      // Selection overlays (mirrored for back so user can still see them)
      if (showSelection) {
        if (isBack) {
          ctx.save()
          ctx.translate(this.canvasWidth, 0)
          ctx.scale(-1, 1)
        }

        // Draw basic meta lines for all unselected items first
        items.forEach((item) => {
          if (item.id !== this.selectedItemId) {
            this.drawItemMeta(ctx, item, false)
          }
        })

        if (this.selectedItemId) {
          const selectedItem = items.find((item) => item.id === this.selectedItemId)

          if (this.dragState && this.dragState.moved) {
            // During drag: Keep empty spaces bright (punched out),
            // only dim the other items so the user can easily see the empty gaps on the paper.
            ctx.fillStyle = 'rgba(0, 0, 0, 0.65)'
            items.forEach((item) => {
              if (item.id !== this.selectedItemId) {
                const drawX = item.x + item.imgX
                const drawY = item.y + item.imgY
                const drawW = item.rotated ? item.imageHeight : item.imageWidth
                const drawH = item.rotated ? item.imageWidth : item.imageHeight
                ctx.fillRect(drawX, drawY, drawW, drawH)
              }
            })
          } else {
            // Normal selection: Dim the entire canvas to highlight the selected item
            ctx.fillStyle = 'rgba(0, 0, 0, 0.65)'
            ctx.beginPath()
            ctx.rect(0, 0, this.canvasWidth, this.canvasHeight)

            if (selectedItem) {
              const drawX = selectedItem.x + selectedItem.imgX
              const drawY = selectedItem.y + selectedItem.imgY
              const drawW = selectedItem.rotated ? selectedItem.imageHeight : selectedItem.imageWidth
              const drawH = selectedItem.rotated ? selectedItem.imageWidth : selectedItem.imageHeight
              ctx.rect(drawX, drawY, drawW, drawH)
            }
            ctx.fill('evenodd')
          }

          if (selectedItem) {
            this.drawItemMeta(ctx, selectedItem, true)

            if (this.dragState) {
              const drawX = selectedItem.x + selectedItem.imgX
              const drawY = selectedItem.y + selectedItem.imgY
              const drawW = selectedItem.rotated ? selectedItem.imageHeight : selectedItem.imageWidth
              const drawH = selectedItem.rotated ? selectedItem.imageWidth : selectedItem.imageHeight

              if (this.isDragOverlapping(selectedItem, selectedItem.x, selectedItem.y)) {
                // Invalid position (overlapping)
                ctx.fillStyle = 'rgba(255, 0, 0, 0.6)'
                ctx.fillRect(drawX, drawY, drawW, drawH)

                ctx.fillStyle = 'white'
                ctx.font = 'bold ' + this.mmToPx(6) + 'px Arial'
                ctx.textAlign = 'center'
                ctx.textBaseline = 'middle'
                ctx.fillText('Overlap!', drawX + drawW / 2, drawY + drawH / 2)
              } else {
                // Valid position
                ctx.fillStyle = 'rgba(0, 255, 0, 0.4)'
                ctx.fillRect(drawX, drawY, drawW, drawH)

                ctx.fillStyle = 'white'
                ctx.font = 'bold ' + this.mmToPx(6) + 'px Arial'
                ctx.textAlign = 'center'
                ctx.textBaseline = 'middle'
                ctx.fillText('Valid', drawX + drawW / 2, drawY + drawH / 2)
              }
            }
          }
        } else {
          // If no selection, just ensure the highlighted borders aren't missed
          items.forEach((item) => {
            this.drawItemMeta(ctx, item, false)
          })
        }

        if (isBack) {
          ctx.restore()
        }
      }

      ctx.restore()
    },

    /** Draw image at its position, supporting 90° CW rotation. */
    drawItem(ctx, item, layer) {
      const img = item.data.layers[layer]
      const drawX = item.x + item.imgX
      const drawY = item.y + item.imgY

      if (item.rotated) {
        this.drawRotatedImage(ctx, img, drawX, drawY, item.imageWidth, item.imageHeight)
      } else {
        this.drawImageOrPlaceholder(ctx, img, drawX, drawY, item.imageWidth, item.imageHeight)
      }
    },

    drawBon(ctx, x, y) {
      const radius = this.mmToPx((Number(this.bonSizeMm) || DEFAULT_BON_SIZE_MM) / 2)
      ctx.beginPath()
      ctx.arc(x, y, radius, 0, 2 * Math.PI)
      ctx.fillStyle = '#000000'
      ctx.fill()
    },

    /**
     * Draw an image rotated 90° CW at position (x, y).
     * On canvas, the rotated image occupies (origH × origW).
     */
    drawRotatedImage(ctx, img, x, y, origW, origH) {
      const canvasW = origH
      const canvasH = origW
      if (img) {
        ctx.save()
        ctx.translate(x + canvasW, y)
        ctx.rotate(Math.PI / 2)
        ctx.drawImage(img, 0, 0, origW, origH)
        ctx.restore()
      } else {
        ctx.fillStyle = '#f0f0f0'
        ctx.fillRect(x, y, canvasW, canvasH)
      }
    },

    drawImageOrPlaceholder(ctx, img, x, y, w, h) {
      if (img) {
        ctx.drawImage(img, x, y, w, h)
        return
      }
      ctx.fillStyle = '#f0f0f0'
      ctx.fillRect(x, y, w, h)
    },

    /** Draw selection/meta info for an item (draw per rect). */
    drawItemMeta(ctx, item, selected) {
      ctx.save()
      ctx.strokeStyle = selected ? '#007bff' : '#999'
      ctx.lineWidth = selected ? 4 : 1
      ctx.setLineDash(selected ? [] : [8, 6])

      for (const r of item.rects) {
        ctx.strokeRect(item.x + r.x, item.y + r.y, r.w, r.h)
      }

      ctx.setLineDash([])
      ctx.restore()
    },

    // ─── Download ──────────────────────────────────────────────────────────────

    downloadCurrentLayer() {
      this.startDownloadWorker(false)
    },
    downloadAllLayers() {
      this.startDownloadWorker(true)
    },
    async startDownloadWorker(isAllLayers) {
      this.isDownloading = true

      // Allow Vue UI to render the loading spinner before running the heavy loop
      await new Promise((resolve) => setTimeout(resolve, 50))

      try {
        const bitmaps = {}
        const transferList = []

        // Optimize: Only extract ImageBitmap for the required pages and layers
        const targetPages = isAllLayers ? this.pages : [this.pages[this.selectedPageIndex]]
        const targetLayers = isAllLayers ? ['front', 'back', 'cut'] : [this.selectedLayer]

        for (let page of targetPages) {
          if (!page) continue
          for (let item of page.items) {
            if (!bitmaps[item.id]) bitmaps[item.id] = {}
            for (let layer of targetLayers) {
              if (item.data.sourceUrls && item.data.sourceUrls[layer]) {
                if (!item.data.layers[layer] && item.data.promises[layer]) {
                  await item.data.promises[layer]
                }
                if (item.data.layers[layer] && !bitmaps[item.id][layer]) {
                  const bmp = await createImageBitmap(item.data.layers[layer])
                  bitmaps[item.id][layer] = bmp
                  transferList.push(bmp)
                }
              }
            }
          }
        }

        const worker = new Worker('/workers/print-artwork-worker.js?v=' + Date.now())

        worker.onmessage = (e) => {
          this.isDownloading = false
          if (e.data.type === 'SUCCESS_ALL') {
            saveAs(e.data.blob, `Artwork-Layers-${Date.now()}.zip`)
          } else if (e.data.type === 'SUCCESS_SINGLE') {
            saveAs(e.data.blob, `Layer-${this.selectedLayer}-Page-${this.selectedPageIndex + 1}-${Date.now()}.png`)
          } else if (e.data.type === 'ERROR') {
            alert('Task failed: ' + e.data.error)
          }
          worker.terminate()
        }

        const pagesData = this.pages.map((p) => ({
          items: p.items.map((it) => ({
            id: it.id,
            x: it.x,
            y: it.y,
            imgX: it.imgX,
            imgY: it.imgY,
            rotated: it.rotated,
            imageWidth: it.imageWidth,
            imageHeight: it.imageHeight
          }))
        }))

        worker.postMessage(
          {
            isAllLayers,
            pages: pagesData,
            canvasWidth: this.canvasWidth,
            canvasHeight: this.canvasHeight,
            bitmaps,
            selectedLayer: this.selectedLayer,
            selectedPageIndex: this.selectedPageIndex,
            dpi: this.dpi,
            bonSizeMm: this.bonSizeMm,
            metadata: {
              paperWidthMm: this.paperWidthMm,
              paperHeightMm: this.paperHeightMm,
              dpi: this.dpi,
              itemSpacingMm: this.marginMm,
              pageMarginMm: this.pageMarginMm,
              bonSizeMm: this.bonSizeMm,
              canvasWidthPx: this.canvasWidth,
              canvasHeightPx: this.canvasHeight,
              totalPages: targetPages.length,
              totalItems: targetPages.reduce((sum, page) => sum + (page?.items?.length || 0), 0),
              layerToExport: isAllLayers ? 'all' : this.selectedLayer,
              preset: this.selectedPreset
            }
          },
          transferList
        )
      } catch (err) {
        console.error(err)
        alert('Could not start download worker: ' + err.message)
        this.isDownloading = false
      }
    }
  }
}
</script>

<style lang="scss">
.print-artwork-form {
  .modal-dialog {
    max-width: 100%;
    margin: auto;
  }
  .modal-header,
  .modal-footer {
    display: none !important;
  }
  .modal-content {
    border-radius: 0;
  }
  .body-container {
    display: flex;
  }
}
</style>
